/*
 * Copyright (c) 2025, Glasswing AI, Inc.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 * Glasswing AI, Inc. ("Confidential Information").
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Glasswing AI, Inc.
 */
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Form, Button, Badge, ButtonToolbar } from 'react-bootstrap';
import { createAiOp, getAiConnectors, setAiOp, AiOp, AiConnector, getAiOp, getAiOps } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';
import { useTheme } from 'styled-components';
import Editor from '@monaco-editor/react';

function AiOpEditor() {
    const [aiOpData, setAiOpData] = useState<{ loading: boolean, data: AiOp | null }>({ loading: true, data: null });
    const [idDup, setIdDup] = useState(false);
    const [availableConnectors, setAvailableConnectors] = useState<AiConnector[]>([]);
    const [existingIds, setExistingIds] = useState<Set<string>>(new Set());
    let popid = useParams<{ opid: string }>().opid;
    const statusContext = useContext<StatusContextType>(StatusContext);
    const theme: any = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchData() {
            try {
                const connectors = await getAiConnectors();
                setAvailableConnectors(connectors);

                if (connectors.length === 0) {
                    statusContext.postError('Please create an AI Connector before creating an AI Operation.');
                    navigate('/ai/connectors');
                    return;
                }

                if (popid === 'new') {
                    setAiOpData({ 
                        loading: false, 
                        data: {
                            id: '',
                            description: 'New AI Operation',
                            type: 'mjs',
                            context_id: connectors[0].id,
                            impl: '',
                            group_folded: false,
                            context: connectors[0],
                        }
                    });
                } else {
                    if (!popid) {
                        throw new Error('Missing AI Operation id');
                    }
                    const opData = await getAiOp(popid);
                    setAiOpData({ loading: false, data: opData });
                }

                const allOps = await getAiOps();
                setExistingIds(new Set(allOps.map((op) => op.id)));
            } catch (error: any) {
                console.error('Error fetching data:', error);
                setAiOpData({ loading: false, data: null });
                statusContext.postError(`Failed to load data: ${error.message}`);
            }
        }
        fetchData();
    }, [popid, navigate, statusContext]);

    const handleChange = (field: keyof AiOp, value: any) => {
        if (aiOpData.data) {
            setAiOpData({
                loading: false,
                data: {
                    ...aiOpData.data,
                    [field]: value
                }
            });
        }
    };

    const handleSave = async () => {
        if (!aiOpData.data) return;
        
        try {
            if (popid === 'new') {
                // Create new operation when save is pressed
                const newId = await createAiOp(aiOpData.data);
                if (newId === "-1") {
                    setIdDup(true);
                    return;
                }
            } else {
                // Update existing operation
                await setAiOp(aiOpData.data);
            }
            navigate(`/ai/ops`);
        } catch (error: any) {
            statusContext.postError(`Failed to save AI Operation: ${error.message}`);
        }
    };

    const handleClone = async () => {
        if (!aiOpData.data || !popid || popid === 'new') {
            return;
        }

        const originId = (aiOpData.data.id || popid || 'operation').trim();
        let defaultCloneName = `${originId}_clone`;
        if (existingIds.has(defaultCloneName)) {
            let n = 2;
            while (existingIds.has(`${originId}_clone${n}`)) {
                n++;
            }
            defaultCloneName = `${originId}_clone${n}`;
        }
        const requestedName = window.prompt('New op name (id)', defaultCloneName);

        if (requestedName === null) {
            return;
        }

        const cloneName = requestedName.trim();
        if (!cloneName) {
            statusContext.postError('Clone cancelled: operation name cannot be empty.');
            return;
        }

        try {
            const cloneData: AiOp = {
                ...aiOpData.data,
                id: cloneName,
            };
            const newId = await createAiOp(cloneData);
            if (newId === '-1') {
                statusContext.postError(`Failed to clone AI Operation: duplicate id "${cloneName}".`);
                return;
            }
            navigate(`/ai/edit_op/${newId}`);
        } catch (error: any) {
            statusContext.postError(`Failed to clone AI Operation: ${error.message}`);
        }
    };

    if (aiOpData.loading) {
        return (
            <Container fluid>
                <Row className="mt-4">
                    <Col>
                        <h2>Loading AI Operation data...</h2>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (!aiOpData.data) {
        return (
            <Container fluid>
                <Row className="mt-4">
                    <Col>
                        <h2>AI Operation not loaded</h2>
                    </Col>
                </Row>
            </Container>
        );
    }

    const { data } = aiOpData;

    return (
        <Container className="px-4" style={{ backgroundColor: theme.colors.bg, color: theme.colors.text }}>
            <Row className="mt-2">
                <Col className="col-6">
                    <h2>AI Operation Editor</h2>
                </Col>
                <Col className="col-6">
                    <ButtonToolbar className="float-end me-5">
                        {popid !== 'new' ? (
                            <Button
                                variant="secondary"
                                onClick={handleClone}
                                className="me-2"
                            >
                                Clone
                            </Button>
                        ) : null}
                        <Button 
                            variant="secondary" 
                            onClick={() => navigate('/ai/ops')} 
                            className="me-2"
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="success" 
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </ButtonToolbar>
                </Col>
            </Row>
            <Row className="mt-4" style={{ height: 'calc(100vh - 48px - 48px)' }}>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Op Name {idDup ? <span style={{ color: 'red' }}> - Duplicate, choose a unique id</span> : null}</Form.Label>
                        <Form.Control
                            type="text"
                            value={data.id || ''}
                            readOnly={popid !== 'new'}
                            onChange={(e) => handleChange('id', e.target.value)}
                            placeholder="Enter op name"
                            style={{ color: theme.colors.ctext }}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            type="text"
                            value={data.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Enter operation description"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>AI Connector</Form.Label>
                        <Form.Select
                            value={data.context_id}
                            onChange={(e) => handleChange('context_id', e.target.value)}
                        >
                            {availableConnectors.map((connector) => (
                                <option key={connector.id} value={connector.id}>
                                    {connector.description}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Op Type</Form.Label>
                        <Form.Select
                            value={data.type}
                            onChange={(e) => handleChange('type', e.target.value)}
                        >
                            <option key="mjs" value="mjs">ESM ECMAScript</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            id="ai-op-group-folded"
                            label="Fold this category to root probes by default"
                            checked={!!data.group_folded}
                            onChange={(e) => handleChange('group_folded', e.target.checked)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Implementation</Form.Label>
                        <Editor
                            height="calc(100vh - 200px)"
                            width='100%'
                            defaultLanguage="javascript"
                            defaultValue="// code goes here"
                            theme={theme.name === 'dark' ? 'vs-dark' : 'light'}
                            options={{
                                minimap: { enabled: false },
                                wordWrap: 'on',
                                automaticLayout: true,
                            }}
                            value={data.impl}
                            onChange={(value) => handleChange('impl', value || '')}
                        />
                    </Form.Group>
                </Col>
            </Row>
        </Container>
    );
}

export default AiOpEditor;